import { useSearchParams } from 'react-router-dom';
import '../css/privacyterm.css'
import '../css/zizaits.css'

function Privacy() {
    const [searchParams] = useSearchParams();
    const language = searchParams.get('language') || '中文';

    document.body.className = 'policy';

    if (language === '英文') {
        return (
            <div className='zizaits-wrap'>
                <p>
                    Welcome to 自在听书 / ZIZAITS (hereinafter referred to as "the App"). The App is developed and operated by
                    Guangzhou Yelizi Network Technology Co., Ltd. (hereinafter referred to as "we"). It is a <strong>fully offline</strong> novel reading and
                    audiobook tool. We attach great importance to your personal information and privacy. Please read this policy carefully
                    before using the App to understand how we handle (or rather, do not handle) your information.
                </p>

                <h3>1. Information We Collect</h3>
                <p>The App runs offline and does <strong>not collect or upload</strong> any personal information:</p>
                <ul>
                    <li><strong>Account information:</strong> No account registration is required. We do not collect identity information such as your name, phone number, or email address.</li>
                    <li><strong>File content:</strong> Books you import (txt / epub / md / pdf, etc.) are stored only in the App's private directory on your device and are never uploaded to any server in any form.</li>
                    <li><strong>Device information:</strong> The App does not send device identifiers, location data, contacts, or similar data to any external server.</li>
                    <li><strong>Audiobook feature:</strong> Speech synthesis is performed by the text-to-speech (TTS) engine built into your device. The text never leaves your device.</li>
                    <li><strong>Crash / statistics:</strong> The App integrates no analytics, crash reporting, advertising, or push SDKs, and therefore reports nothing about crashes or usage.</li>
                </ul>

                <h3>2. Permissions We Request</h3>
                <p>
                    The App only requests the minimum permissions required for its features. All permissions are requested with an on-screen
                    explanation and take effect only after you grant them. You may withdraw them at any time in your system settings; doing so
                    only disables the related feature and does not affect the rest of the App.
                </p>
                <table border={1}>
                    <tr>
                        <td>Permission</td>
                        <td>Purpose</td>
                        <td>Involved information</td>
                    </tr>
                    <tr>
                        <td>Read external storage (READ_EXTERNAL_STORAGE)</td>
                        <td>Import books (txt / epub / md / pdf, etc.) from your device</td>
                        <td>Local files you actively select; nothing is uploaded</td>
                    </tr>
                    <tr>
                        <td>Write external storage (WRITE_EXTERNAL_STORAGE)</td>
                        <td>Export books, and save the support QR code to your album</td>
                        <td>Local files written by the App; nothing is uploaded</td>
                    </tr>
                    <tr>
                        <td>Foreground service (FOREGROUND_SERVICE / MEDIA_PLAYBACK)</td>
                        <td>Keep audiobook playback running in the background and show playback controls in the notification area</td>
                        <td>No personal information involved</td>
                    </tr>
                    <tr>
                        <td>Media / notifications</td>
                        <td>Show audiobook playback controls and progress in the notification bar, on the lock screen and via headsets</td>
                        <td>No personal information involved</td>
                    </tr>
                    <tr>
                        <td>Camera (CAMERA)</td>
                        <td>Declared but not used by any current feature; it is never invoked at runtime and never reads any image</td>
                        <td>None</td>
                    </tr>
                    <tr>
                        <td>Internet (INTERNET)</td>
                        <td>Declared but unused: the App sends no data to any server and does not load any web page</td>
                        <td>None</td>
                    </tr>
                </table>

                <h3>3. Personal Information Disclosure List</h3>
                <table className="list" border={1}>
                    <tr>
                        <th colSpan={4}>自在听书 App Personal Information Disclosure List</th>
                    </tr>
                    <tr>
                        <td>Scenario</td>
                        <td>Purpose</td>
                        <td>Collection method</td>
                        <td>Information fields</td>
                    </tr>
                    <tr>
                        <td>Importing local books</td>
                        <td>Reading the book files you select</td>
                        <td>User selects a file + system permission</td>
                        <td>Local book files (txt / epub / md / pdf). Stored on the device only; never uploaded</td>
                    </tr>
                    <tr>
                        <td>Reading / audiobook playback</td>
                        <td>Saving reading progress and playback settings (voice, pitch, speed)</td>
                        <td>Generated locally by the App</td>
                        <td>Reading records locally; no personal information involved</td>
                    </tr>
                    <tr>
                        <td>Saving the support QR code</td>
                        <td>Saving the customer service QR code image to your album</td>
                        <td>User taps Save + system permission</td>
                        <td>One locally generated image; never uploaded</td>
                    </tr>
                    <tr>
                        <td>Customer support</td>
                        <td>Handling the questions you raise on your own initiative</td>
                        <td>Provided by you via email / WeChat</td>
                        <td>Communication content and your contact details; used only to reply to you</td>
                    </tr>
                </table>

                <h3>4. Third-Party SDKs and Services</h3>
                <table className="list" border={1}>
                    <tr>
                        <td>Name</td>
                        <td>Provider</td>
                        <td>Information involved</td>
                        <td>Purpose</td>
                    </tr>
                    <tr>
                        <td>System text-to-speech engine (Android TextToSpeech)</td>
                        <td>Provided by your device manufacturer / system</td>
                        <td>The text to be read aloud, passed in when called; processed entirely on the device</td>
                        <td>Converting text to speech for the audiobook feature</td>
                    </tr>
                    <tr>
                        <td>AndroidX Media (media session)</td>
                        <td>Google LLC</td>
                        <td>None</td>
                        <td>Audiobook playback controls in the notification bar / lock screen</td>
                    </tr>
                </table>
                <p>
                    We do <strong>not</strong> integrate any third-party analytics, advertising, push-notification, payment, login, or
                    crash-reporting SDK. We do not share, transfer, sell, or rent your information to any third party.
                </p>

                <h3>5. Storage and Security of Information</h3>
                <p>
                    Your books, reading progress, and audiobook settings are all stored locally on your device. We rely on the system's
                    sandbox isolation to protect this data; no other application can access it without authorization. We keep no server-side
                    storage of your data and maintain no backend user database.
                </p>

                <h3>6. Sharing, Transfer and Public Disclosure</h3>
                <p>We will not share your personal information with any company, organization, or individual, except where:</p>
                <ul>
                    <li>We have obtained your explicit consent in advance;</li>
                    <li>It is required by applicable laws and regulations, or required by judicial or administrative authorities in accordance with the law.</li>
                </ul>

                <h3>7. Your Rights</h3>
                <ul>
                    <li><strong>Access and correction:</strong> Your data exists only on your device and can be viewed or modified directly inside the App.</li>
                    <li><strong>Deletion:</strong> You may clear all local data of the App at any time through your system's App management; uninstalling the App completely deletes all imported books and settings.</li>
                    <li><strong>Withdrawing authorization:</strong> You may revoke any permission at any time in Settings → Apps → 自在听书 → Permissions.</li>
                    <li><strong>Account cancellation:</strong> There is no registration or login; we hold no cloud data about you, so no account cancellation process is required.</li>
                </ul>

                <h3>8. Protection of Minors</h3>
                <p>
                    The App is not directed at children under 14. We do not knowingly collect any personal information from minors. If a
                    guardian finds that a minor has provided information to us, please contact us using the details below and we will handle it promptly.
                </p>

                <h3>9. Changes to This Policy</h3>
                <p>
                    We may update this policy from time to time. When we make material changes, we will present them within the App and indicate
                    the new effective date. Please check back regularly to stay informed about how we protect your privacy.
                </p>

                <h3>10. Contact Us</h3>
                <p>If you have any questions or suggestions about this policy, please contact us:</p>
                <ul>
                    <li>Email: pengdeshen_2013@qq.com</li>
                    <li>WeChat: yelizibiaoge (or open Profile → Contact Us in the App)</li>
                    <li>Developer: Guangzhou Yelizi Network Technology Co., Ltd.</li>
                </ul>
                <p>We will respond within 15 working days of receiving your request.</p>

                <p>Thank you for using 自在听书. We are dedicated to providing you with a safe and pure offline reading experience!</p>
                <p style={{ marginTop: '20px', fontSize: 14 }}>Last updated: October 10, 2026<br />Effective date: October 10, 2026</p>
            </div>
        )
    }

    if (language === '繁体') {
        return (
            <div className='zizaits-wrap'>
                <p>
                    歡迎使用「自在聽書」（以下簡稱「本應用」）。本應用由廣州野離子網絡科技有限公司（以下簡稱「我們」）研發並營運，
                    是一款<strong>完全離線</strong>的小說閱讀與聽書工具。我們深知個人資訊對你的重要性，並承諾保護你的隱私。
                    請你在使用本應用前仔細閱讀本政策，瞭解我們如何處理（或更準確地說，如何不處理）你的資訊。
                </p>

                <h3>一、我們蒐集的資訊</h3>
                <p>本應用離線運行，<strong>不蒐集、不上傳</strong>你的任何個人資訊：</p>
                <ul>
                    <li><strong>帳號資訊：</strong>使用本應用無須註冊帳號，我們不蒐集姓名、手機號碼、電子郵件等身分資訊。</li>
                    <li><strong>檔案內容：</strong>你匯入的書籍（txt / epub / md / pdf 等）僅儲存於你裝置上的應用程式私有目錄，不會以任何形式上傳至伺服器。</li>
                    <li><strong>裝置資訊：</strong>本應用不會向外部伺服器傳送裝置識別碼、位置、通訊錄等資料。</li>
                    <li><strong>聽書功能：</strong>語音合成由裝置系統內建的文字轉語音（TTS）引擎完成，文字不會離開你的裝置。</li>
                    <li><strong>崩潰 / 統計：</strong>本應用未整合任何統計、崩潰回報、廣告或推播 SDK，因此不會回報任何使用情況。</li>
                </ul>

                <h3>二、我們申請的權限</h3>
                <p>
                    本應用僅申請實現功能所必需的最少權限，且均會在使用時彈出說明並經你授權後啟用。
                    你可隨時在系統設定中撤回授權；撤回只會停用對應功能，不影響其他功能使用。
                </p>
                <table border={1}>
                    <tr>
                        <td>權限名稱</td>
                        <td>使用目的</td>
                        <td>涉及資訊</td>
                    </tr>
                    <tr>
                        <td>讀取外部儲存空間（READ_EXTERNAL_STORAGE）</td>
                        <td>從裝置匯入書籍（txt / epub / md / pdf 等）</td>
                        <td>你主動選取的本地檔案，不上傳</td>
                    </tr>
                    <tr>
                        <td>寫入外部儲存空間（WRITE_EXTERNAL_STORAGE）</td>
                        <td>匯出書籍、將客服 QR Code 儲存至相簿</td>
                        <td>本應用寫入的本地檔案，不上傳</td>
                    </tr>
                    <tr>
                        <td>前景服務（FOREGROUND_SERVICE / MEDIA_PLAYBACK）</td>
                        <td>讓聽書在背景持續播放，並在通知欄提供播放控制</td>
                        <td>不涉及任何個人資訊</td>
                    </tr>
                    <tr>
                        <td>媒體 / 通知</td>
                        <td>在通知欄、鎖屏與耳機端顯示聽書播放控制與進度</td>
                        <td>不涉及任何個人資訊</td>
                    </tr>
                    <tr>
                        <td>相機（CAMERA）</td>
                        <td>僅作宣告，目前沒有任何功能使用；不會在運行時呼叫，也不讀取任何影像</td>
                        <td>無</td>
                    </tr>
                    <tr>
                        <td>網路（INTERNET）</td>
                        <td>僅作宣告且未使用：本應用不向任何伺服器傳送資料，也不載入任何網頁</td>
                        <td>無</td>
                    </tr>
                </table>

                <h3>三、個人資訊明示清單</h3>
                <table className="list" border={1}>
                    <tr>
                        <th colSpan={4}>自在聽書 App 個人資訊明示清單</th>
                    </tr>
                    <tr>
                        <td>業務場景</td>
                        <td>蒐集目的</td>
                        <td>蒐集方式</td>
                        <td>個人資訊欄位</td>
                    </tr>
                    <tr>
                        <td>匯入本地書籍</td>
                        <td>讀取你選取的書籍檔案</td>
                        <td>使用者選取檔案 + 系統授權</td>
                        <td>本地書籍檔案（txt / epub / md / pdf），僅存於本機，不上傳</td>
                    </tr>
                    <tr>
                        <td>閱讀 / 聽書播放</td>
                        <td>保存閱讀進度與聽書設定（發音人、音高、倍速）</td>
                        <td>本應用本地生成</td>
                        <td>本地閱讀記錄，不涉及個人資訊</td>
                    </tr>
                    <tr>
                        <td>儲存客服 QR Code</td>
                        <td>將客服 QR Code 圖片儲存到相簿</td>
                        <td>使用者點擊儲存 + 系統授權</td>
                        <td>本地生成的一張圖片，不上傳</td>
                    </tr>
                    <tr>
                        <td>客服諮詢</td>
                        <td>處理你主動提出的問題</td>
                        <td>你透過郵件 / 微信主動提供</td>
                        <td>溝通內容與你的聯絡方式，僅用於回覆你</td>
                    </tr>
                </table>

                <h3>四、第三方 SDK 與服務清單</h3>
                <table className="list" border={1}>
                    <tr>
                        <td>名稱</td>
                        <td>提供方</td>
                        <td>涉及個人資訊</td>
                        <td>使用目的</td>
                    </tr>
                    <tr>
                        <td>系統文字轉語音引擎（Android TextToSpeech）</td>
                        <td>裝置廠商 / 系統提供</td>
                        <td>呼叫時傳入的待朗讀文本，全程在裝置本地處理</td>
                        <td>聽書功能的文字轉語音合成</td>
                    </tr>
                    <tr>
                        <td>AndroidX Media（媒體會話）</td>
                        <td>Google LLC</td>
                        <td>不涉及</td>
                        <td>通知欄 / 鎖屏的聽書播放控制</td>
                    </tr>
                </table>
                <p>
                    我們<strong>沒有</strong>整合任何第三方統計、廣告、推播、支付、登入或崩潰回報 SDK，
                    也不向任何第三方共享、轉讓、出售或出租你的資訊。
                </p>

                <h3>五、資訊的儲存與保護</h3>
                <p>
                    你的書籍、閱讀進度與聽書設定均儲存在裝置本地。我們依託系統提供的沙盒隔離機制保護資料安全，
                    未經授權其他應用程式無法存取。我們沒有伺服器端儲存，也不維護任何使用者資料庫。
                </p>

                <h3>六、資訊的共享、轉讓與公開揭露</h3>
                <p>除下列情形外，我們不會向任何公司、組織或個人共享你的個人資訊：</p>
                <ul>
                    <li>事先取得你的明確同意；</li>
                    <li>依據適用法律法規的要求，或司法、行政機關依法定的要求。</li>
                </ul>

                <h3>七、你的權利</h3>
                <ul>
                    <li><strong>查閱與更正：</strong>你的資料僅存在於本機，可直接在應用內查閱或修改。</li>
                    <li><strong>刪除：</strong>你可隨時透過系統的應用程式管理清除本應用的全部本地資料；解除安裝應用即可徹底刪除所有匯入書籍與設定。</li>
                    <li><strong>撤回授權：</strong>你可隨時在 設定 → 應用程式 → 自在聽書 → 權限 中關閉已授予的權限。</li>
                    <li><strong>註銷帳號：</strong>本應用無註冊登入機制，我們不持有你的任何雲端資料，因此無須額外的註銷流程。</li>
                </ul>

                <h3>八、未成年人保護</h3>
                <p>
                    本應用並非面向未滿 14 週歲兒童提供。我們不會有意蒐集未成年人的個人資訊。
                    若監護人發現未成年人向我們提供了資訊，請透過下方方式聯絡我們，我們會及時處理。
                </p>

                <h3>九、政策的變更</h3>
                <p>
                    我們可能不時更新本政策。當發生重大變更時，我們將在本應用內展示並注明新的生效日期。
                    請定期查看，以確保你瞭解我們的隱私保護措施。
                </p>

                <h3>十、聯絡我們</h3>
                <p>若你對本政策有任何疑問或建議，歡迎透過以下方式與我們聯絡：</p>
                <ul>
                    <li>電子郵件：pengdeshen_2013@qq.com</li>
                    <li>微信：yelizibiaoge（或在應用內「聯絡我們」取得客服方式）</li>
                    <li>開發營運主體：廣州野離子網絡科技有限公司</li>
                </ul>
                <p>我們將在收到你的請求後 15 個工作日內予以回應。</p>

                <p>感謝你使用自在聽書，我們將竭誠為你提供安全、純淨的離線閱讀體驗！</p>
                <p style={{ marginTop: '20px', fontSize: 14 }}>更新日期：2026 年 10 月 10 日<br />生效日期：2026 年 10 月 10 日</p>
            </div>
        )
    }

    return (
        <div className='zizaits-wrap'>
            <p>
                欢迎使用「自在听书」（以下简称“本应用”）。本应用由广州野离子网络科技有限公司（以下简称“我们”）研发并运营，
                是一款<strong>完全离线</strong>的小说阅读与听书工具。我们深知个人信息对你的重要性，并承诺保护你的隐私。
                请你在使用本应用前仔细阅读本政策，了解我们如何处理（或更准确地说，如何不处理）你的信息。
            </p>

            <h3>一、我们收集的信息</h3>
            <p>本应用离线运行，<strong>不收集、不上传</strong>你的任何个人信息：</p>
            <ul>
                <li><strong>账号信息：</strong>使用本应用无需注册账号，我们不收集姓名、手机号、邮箱等身份信息。</li>
                <li><strong>文件内容：</strong>你导入的书籍（txt / epub / md / pdf 等）仅存储于你设备上的应用私有目录，不会以任何形式上传至服务器。</li>
                <li><strong>设备信息：</strong>本应用不会向外部服务器发送设备标识符、位置、通讯录等数据。</li>
                <li><strong>听书功能：</strong>语音合成由设备系统自带的文本转语音（TTS）引擎完成，文本不会离开你的设备。</li>
                <li><strong>崩溃 / 统计：</strong>本应用未集成任何统计、崩溃上报、广告或推送 SDK，因此不会上报任何使用情况。</li>
            </ul>

            <h3>二、我们申请的权限</h3>
            <p>
                本应用仅申请实现功能所必需的最少权限，且均会在使用时弹出说明并经你授权后启用。
                你可随时在系统设置中撤回授权；撤回只会导致对应功能不可用，不影响其他功能使用。
            </p>
            <table className="menu" border={1}>
                <tr>
                    <td>权限名称</td>
                    <td>使用目的</td>
                    <td>涉及信息</td>
                </tr>
                <tr>
                    <td>读取外置存储空间（READ_EXTERNAL_STORAGE）</td>
                    <td>从设备导入书籍（txt / epub / md / pdf 等）</td>
                    <td>你主动选取的本地文件，不上传</td>
                </tr>
                <tr>
                    <td>写入外置存储空间（WRITE_EXTERNAL_STORAGE）</td>
                    <td>导出书籍、将客服二维码保存至相册</td>
                    <td>本应用写入的本地文件，不上传</td>
                </tr>
                <tr>
                    <td>前台服务（FOREGROUND_SERVICE / MEDIA_PLAYBACK）</td>
                    <td>让听书在后台持续播放，并在通知栏提供播放控制</td>
                    <td>不涉及任何个人信息</td>
                </tr>
                <tr>
                    <td>媒体 / 通知</td>
                    <td>在通知栏、锁屏与耳机端显示听书播放控制与进度</td>
                    <td>不涉及任何个人信息</td>
                </tr>
                <tr>
                    <td>相机（CAMERA）</td>
                    <td>仅作声明，目前没有任何功能使用；不会在运行时调用，也不读取任何影像</td>
                    <td>无</td>
                </tr>
                <tr>
                    <td>网络（INTERNET）</td>
                    <td>仅作声明且未使用：本应用不向任何服务器发送数据，也不加载任何网页</td>
                    <td>无</td>
                </tr>
            </table>

            <h3>三、个人信息明示清单</h3>
            <table className="list" border={1}>
                <tr>
                    <th colSpan={4}>自在听书App个人信息明示清单</th>
                </tr>
                <tr>
                    <td>业务场景</td>
                    <td>收集目的</td>
                    <td>收集方式</td>
                    <td>个人信息字段</td>
                </tr>
                <tr>
                    <td>导入本地书籍</td>
                    <td>读取你选取的书籍文件</td>
                    <td>用户选取文件 + 系统授权</td>
                    <td>本地书籍文件（txt / epub / md / pdf），仅存于本机，不上传</td>
                </tr>
                <tr>
                    <td>阅读 / 听书播放</td>
                    <td>保存阅读进度与听书设置（发音人、音高、倍速）</td>
                    <td>本应用本地生成</td>
                    <td>本地阅读记录，不涉及个人信息</td>
                </tr>
                <tr>
                    <td>保存客服二维码</td>
                    <td>将客服二维码图片保存到相册</td>
                    <td>用户点击保存 + 系统授权</td>
                    <td>本地生成的一张图片，不上传</td>
                </tr>
                <tr>
                    <td>客服咨询</td>
                    <td>处理你主动提出的问题</td>
                    <td>你通过邮件 / 微信主动提供</td>
                    <td>沟通内容与你的联系方式，仅用于回复你</td>
                </tr>
            </table>

            <h3>四、第三方SDK与服务清单</h3>
            <table className="list" border={1}>
                <tr>
                    <td>名称</td>
                    <td>提供方</td>
                    <td>涉及个人信息</td>
                    <td>使用目的</td>
                </tr>
                <tr>
                    <td>系统文本转语音引擎（Android TextToSpeech）</td>
                    <td>设备厂商 / 系统提供</td>
                    <td>调用时传入的待朗读文本，全程在设备本地处理</td>
                    <td>听书功能的文字转语音合成</td>
                </tr>
                <tr>
                    <td>AndroidX Media（媒体会话）</td>
                    <td>Google LLC</td>
                    <td>不涉及</td>
                    <td>通知栏 / 锁屏的听书播放控制</td>
                </tr>
            </table>
            <p>
                我们<strong>没有</strong>集成任何第三方统计、广告、推送、支付、登录或崩溃上报 SDK，
                也不向任何第三方共享、转让、出售或出租你的信息。
            </p>

            <h3>五、信息的存储与保护</h3>
            <p>
                你的书籍、阅读进度与听书设置均保存在设备本地。我们依托系统提供的沙盒隔离机制保护数据安全，
                未经授权其他应用无法访问。我们没有服务器端存储，也不维护任何用户数据库。
            </p>

            <h3>六、信息的共享、转让与公开披露</h3>
            <p>除下列情形外，我们不会向任何公司、组织或个人共享你的个人信息：</p>
            <ul>
                <li>事先取得你的明确同意；</li>
                <li>依据适用法律法规的要求，或司法、行政机关依法定程序的要求。</li>
            </ul>

            <h3>七、你的权利</h3>
            <ul>
                <li><strong>查阅与更正：</strong>你的数据仅存在于本机，可直接在应用内查阅或修改。</li>
                <li><strong>删除：</strong>你可随时通过系统的应用管理清除本应用的全部本地数据；卸载应用即可彻底删除所有导入书籍与设置。</li>
                <li><strong>撤回授权：</strong>你可随时在 设置 → 应用 → 自在听书 → 权限 中关闭已授予的权限。</li>
                <li><strong>注销账号：</strong>本应用无注册登录机制，我们不持有你的任何云端数据，因此无需额外的注销流程。</li>
            </ul>

            <h3>八、未成年人保护</h3>
            <p>
                本应用并非面向未满 14 周岁的儿童提供。我们不会有意收集未成年人的个人信息。
                若监护人发现未成年人向我们提供了信息，请通过下方方式联系我们，我们会及时处理。
            </p>

            <h3>九、政策的变更</h3>
            <p>
                我们可能不时更新本政策。当发生重大变更时，我们将在本应用内展示并注明新的生效日期。
                请定期查看，以确保你了解我们的隐私保护措施。
            </p>

            <h3>十、联系我们</h3>
            <p>若你对本政策有任何疑问或建议，欢迎通过以下方式与我们联系：</p>
            <ul>
                <li>电子邮箱：pengdeshen_2013@qq.com</li>
                <li>微信：yelizibiaoge（或在应用内「联系我们」获取客服方式）</li>
                <li>开发运营主体：广州野离子网络科技有限公司</li>
            </ul>
            <p>我们将在收到你的请求后 15 个工作日内予以答复。</p>

            <p>感谢你使用自在听书，我们将竭诚为你提供安全、纯净的离线阅读体验！</p>
            <p style={{ marginTop: '20px', fontSize: 14 }}>更新日期：2026年10月10日<br />生效日期：2026年10月10日</p>
        </div>
    )
}

export default Privacy;
